import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import ResetPasswordDialog from '../components/auth/ResetPasswordDialog';
import RoleSelection from '../components/auth/RoleSelection';

const AuthPage = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const preselectedRole = location.state?.role || urlParams.get('role') || '';
  const joinMode = urlParams.get('mode') === 'join';
  const inviteCode = urlParams.get('code') || '';
  const [isSignUp, setIsSignUp] = useState(!!preselectedRole || joinMode || !!inviteCode);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: preselectedRole || ((joinMode || inviteCode) ? 'care_partner' : ''),
    care_recipient_first_name: '',
    care_recipient_email: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const notifyJoinResult = (joinedCircle) => {
    if (joinedCircle) {
      toast.success(`You've been added to ${joinedCircle.name || 'the care circle'}!`);
    } else if (inviteCode) {
      toast.error(`We couldn't find a circle for code ${inviteCode.toUpperCase()} — you can enter it again during setup.`);
    }
  };

  // Patients sign up → SupportOnboarding wizard creates their circle → wizard onComplete
  // redirects them to /welcome (Calendly). Coordinators skip the wizard and land on /welcome
  // directly because their circle is auto-created at signup.
  const postSignupRoute = (role) => (role === 'care_coordinator') ? '/welcome' : '/dashboard';

  const handleRoleSelect = async (role) => {
    // Coordinator flow needs recipient info; bounce back to the form to collect it.
    if (role === 'care_coordinator') {
      setFormData({ ...formData, role });
      setShowRoleSelection(false);
      return;
    }
    setShowRoleSelection(false);
    setLoading(true);
    try {
      // Strip coordinator-only fields so Pydantic EmailStr doesn't reject empty strings.
      const { care_recipient_first_name, care_recipient_email, ...cleanForm } = formData;
      const response = await axios.post(`${API}/auth/signup`, { ...cleanForm, role, invite_code: inviteCode });
      toast.success('Welcome to CircleUpSquad!');
      notifyJoinResult(response.data.joined_circle);
      flushSync(() => login(response.data.user, true, inviteCode));
      navigate(postSignupRoute(role));
    } catch (error) { toast.error(error.response?.data?.detail || 'Something went wrong'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignUp) {
        if (!formData.role) { setShowRoleSelection(true); setLoading(false); return; }
        if (formData.role === 'care_coordinator' && !formData.care_recipient_first_name.trim()) {
          toast.error("Please add the first name of the person you're caring for."); setLoading(false); return;
        }
        const payload = { ...formData, invite_code: inviteCode };
        if (formData.role !== 'care_coordinator') { delete payload.care_recipient_first_name; delete payload.care_recipient_email; }
        else if (!payload.care_recipient_email || !payload.care_recipient_email.trim()) { delete payload.care_recipient_email; }
        const response = await axios.post(`${API}/auth/signup`, payload);
        toast.success('Welcome to CircleUpSquad!');
        notifyJoinResult(response.data.joined_circle);
        if (response.data.coordinator_circle) toast.success(`Created ${response.data.coordinator_circle.name}`);
        flushSync(() => login(response.data.user, true, inviteCode));
        navigate(postSignupRoute(formData.role));
      } else {
        const response = await axios.post(`${API}/auth/login`, { email: formData.email, password: formData.password, invite_code: inviteCode });
        toast.success('Welcome back!');
        if (response.data.joined_circle) toast.success(`You've been added to ${response.data.joined_circle.name || 'the care circle'}!`);
        login(response.data.user);
        navigate('/dashboard');
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Something went wrong'); } finally { setLoading(false); }
  };

  const handleForgotPasswordRequest = async () => {
    if (!resetEmail) { toast.error('Enter your email first'); return; }
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      setGeneratedCode(response.data.code);
      toast.success('Reset code generated');
      setForgotPasswordStep(2);
    } catch (error) { toast.error('Could not generate code'); }
  };

  const handleVerifyCode = async () => {
    if (!resetCode) { toast.error('Enter the code'); return; }
    try {
      await axios.post(`${API}/auth/verify-reset-code`, { email: resetEmail, code: resetCode });
      toast.success('Code verified');
      setForgotPasswordStep(3);
    } catch (error) { toast.error(error.response?.data?.detail || 'Invalid code'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await axios.post(`${API}/auth/reset-password`, { email: resetEmail, code: resetCode, new_password: newPassword });
      toast.success('Password reset! Please sign in.');
      setShowForgotPassword(false);
      setForgotPasswordStep(1); setResetEmail(''); setResetCode(''); setGeneratedCode(''); setNewPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset'); }
  };

  if (showRoleSelection) {
    return <RoleSelection onSelect={handleRoleSelect} onBack={() => setShowRoleSelection(false)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="auth-page">
      <div className="max-w-sm w-full fade-in">
        <div className="surface-card p-7 shadow-lg">
          <div className="text-center mb-6">
            <div className="relative w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40" />
              <div className="absolute inset-2 rounded-full border-2 border-cyan-400/60" />
              <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isSignUp ? 'Join CircleUpSquad' : 'Sign in to your care circle'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="name" name="name" data-testid="name-input" placeholder="Your name" value={formData.name} onChange={handleInputChange} required={isSignUp} className="pl-10 rounded-xl" />
                </div>
              </div>
            )}

            {isSignUp && formData.role === 'care_coordinator' && (
              <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-xl border border-purple-100 dark:border-purple-900/40" data-testid="coordinator-recipient-fields">
                <p className="text-xs text-purple-700 dark:text-purple-300 font-medium uppercase tracking-wide">About the person you're caring for</p>
                <div className="space-y-1.5">
                  <Label htmlFor="care_recipient_first_name" className="text-sm font-medium">Their first name</Label>
                  <Input id="care_recipient_first_name" name="care_recipient_first_name" data-testid="recipient-first-name-input"
                    placeholder="e.g. Mom, or Elena"
                    value={formData.care_recipient_first_name} onChange={handleInputChange} required className="rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="care_recipient_email" className="text-sm font-medium">Their email <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="care_recipient_email" name="care_recipient_email" type="email" data-testid="recipient-email-input"
                    placeholder="If they want their own login later"
                    value={formData.care_recipient_email} onChange={handleInputChange} className="rounded-xl" />
                  <p className="text-xs text-muted-foreground">We'll email them a link to join their own circle when they're ready. You can add this later too.</p>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" name="email" type="email" data-testid="email-input" placeholder="you@example.com" value={formData.email} onChange={handleInputChange} required className="pl-10 rounded-xl" />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                {!isSignUp && (
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-cyan-600 hover:text-cyan-700 font-medium transition-colors">Forgot?</button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" name="password" type={showPassword ? 'text' : 'password'} data-testid="password-input" placeholder="Your password" value={formData.password} onChange={handleInputChange} required className="pl-10 pr-10 rounded-xl" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" data-testid="auth-submit-btn" className="w-full btn-primary" disabled={loading}>
              {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </Button>
          </form>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="mt-4">
            <div className="relative flex items-center gap-3 my-3" aria-hidden="true">
              <div className="flex-1 h-px bg-border/60" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70">or</span>
              <div className="flex-1 h-px bg-border/60" />
            </div>
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full h-11 rounded-xl border border-border bg-background hover:bg-slate-50 dark:hover:bg-slate-900/50 flex items-center justify-center gap-3 text-sm font-semibold text-foreground transition-colors"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C41 34.6 44 29.8 44 24c0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="mt-5 text-center">
            <button onClick={() => setIsSignUp(!isSignUp)} className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors">
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>
        </div>
      </div>

      <ResetPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        step={forgotPasswordStep}
        resetEmail={resetEmail}
        setResetEmail={setResetEmail}
        resetCode={resetCode}
        setResetCode={setResetCode}
        generatedCode={generatedCode}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onRequest={handleForgotPasswordRequest}
        onVerify={handleVerifyCode}
        onReset={handleResetPassword}
        onCancel={() => { setShowForgotPassword(false); setForgotPasswordStep(1); setResetEmail(''); setResetCode(''); setGeneratedCode(''); setNewPassword(''); }}
      />
    </div>
  );
};

export default AuthPage;
